import { useState } from 'react';

const industries = [
  {
    name: 'Energy',
    icon: 'ri-flashlight-line',
    gradient: 'from-yellow-500 to-orange-500',
    description: 'Smart grid automation, predictive maintenance, and energy optimization workflows',
    solutions: ['Asset Management', 'Demand Forecasting', 'Grid Optimization'],
  },
  {
    name: 'Logistics',
    icon: 'ri-truck-line',
    gradient: 'from-blue-500 to-cyan-500',
    description: 'End-to-end supply chain automation with real-time tracking and optimization',
    solutions: ['Route Optimization', 'Inventory Sync', 'Shipment Tracking'],
  },
  {
    name: 'Legal',
    icon: 'ri-scales-3-line',
    gradient: 'from-purple-500 to-pink-500',
    description: 'Contract analysis, document automation, and compliance monitoring',
    solutions: ['Contract Review', 'Due Diligence', 'Compliance Checks'],
  },
  {
    name: 'Manufacturing',
    icon: 'ri-settings-3-line',
    gradient: 'from-green-500 to-emerald-500',
    description: 'Production line automation, quality control, and supply chain integration',
    solutions: ['Quality Assurance', 'Production Planning', 'Maintenance Scheduling'],
  },
  {
    name: 'Finance',
    icon: 'ri-line-chart-line',
    gradient: 'from-indigo-500 to-violet-500',
    description: 'Automated reporting, risk assessment, and regulatory compliance',
    solutions: ['Risk Analysis', 'Fraud Detection', 'Regulatory Reporting'],
  },
];

export default function IndustrySolutions() {
  const [selectedIndustry, setSelectedIndustry] = useState<number | null>(null);
  const [activeIndustry, setActiveIndustry] = useState(0);

  const handleDiscussClick = () => {
    const leadSection = document.getElementById('lead-capture-section');
    if (leadSection) {
      leadSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative py-32 px-6 bg-gradient-to-b from-[#1a0b2e] to-[#0a0a0f]">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-purple-500/10 blur-3xl"
          style={{ animation: 'float 8s ease-in-out infinite' }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl"
          style={{ animation: 'float 10s ease-in-out infinite reverse' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-black mb-6">
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Industry Solutions
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Tailored automation strategies for your specific industry
          </p>
        </div>

        {/* Industry Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {industries.map((industry, index) => (
            <div
              key={index}
              className="group relative cursor-pointer"
              onClick={() => setSelectedIndustry(selectedIndustry === index ? null : index)}
            >
              <div
                className={`relative h-full p-8 rounded-2xl border transition-all duration-500 ${
                  selectedIndustry === index
                    ? 'border-purple-400/60 bg-gradient-to-br from-purple-900/30 to-cyan-900/20'
                    : 'border-purple-500/20 bg-gradient-to-br from-purple-900/10 to-transparent hover:border-purple-400/40'
                }`}
                style={{
                  transform: selectedIndustry === index ? 'scale(1.02)' : 'scale(1)',
                  boxShadow:
                    selectedIndustry === index
                      ? '0 20px 60px rgba(139, 92, 246, 0.3)'
                      : '0 4px 20px rgba(0, 0, 0, 0.3)',
                }}
              >
                {/* Icon */}
                <div className="mb-6">
                  <div
                    className={`w-20 h-20 flex items-center justify-center rounded-2xl bg-gradient-to-br ${industry.gradient} transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6`}
                  >
                    <i className={`${industry.icon} text-4xl text-white`} />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-3xl font-black text-white mb-4">{industry.name}</h3>

                {/* Description */}
                <p className="text-gray-400 mb-6 leading-relaxed">{industry.description}</p>

                {/* Solutions List */}
                <div
                  className="overflow-hidden transition-all duration-500"
                  style={{
                    maxHeight: selectedIndustry === index ? '200px' : '0',
                    opacity: selectedIndustry === index ? 1 : 0,
                  }}
                >
                  <div className="pt-4 border-t border-purple-500/30">
                    <p className="text-sm font-semibold text-purple-400 mb-3">Key Solutions:</p>
                    <ul className="space-y-2">
                      {industry.solutions.map((solution, sIndex) => (
                        <li key={sIndex} className="flex items-center gap-2 text-gray-300">
                          <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${industry.gradient}`} />
                          <span className="text-sm">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Expand Indicator */}
                <div className="mt-6 flex items-center justify-end">
                  <i
                    className={`ri-arrow-${selectedIndustry === index ? 'up' : 'down'}-s-line text-purple-400 text-2xl transition-transform duration-300`}
                  />
                </div>

                {/* Hover Glow */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${industry.gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500 pointer-events-none`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <p className="text-xl text-gray-400 mb-6">
            Don't see your industry? We create custom solutions for any sector.
          </p>
          <button
            onClick={handleDiscussClick}
            className="group px-10 py-5 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-bold text-lg hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center gap-2">
              Discuss Your Industry
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300" />
            </span>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-30px);
          }
        }
      `}</style>
    </section>
  );
}
